import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Package,
  ExternalLink,
  MapPin,
  Clock,
  CheckCircle2,
  Truck,
  ArrowRight,
  Plane,
  Phone,
  Mail,
  Building2,
  Globe,
  User,
  FileText,
  Copy,
  Info,
  Calculator,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { getDhlTracking, getDhlRates, getDhlServicePoints } from '../api';
import { getDhlTrackingUrl } from '../types';
import type { DhlTrackingShipment, DhlTrackingEvent, DhlRateResult, DhlServicePoint } from '../types';

// =============================================================================
// Contact & Account Info Tab
// =============================================================================

function copyToClipboard(text: string, label: string) {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copied`);
}

function ContactInfoRow({ icon: Icon, label, value, href, copyable = true }: {
  icon: typeof Phone;
  label: string;
  value: string;
  href?: string;
  copyable?: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-3 border-b last:border-0">
      <div className="flex items-center gap-3">
        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
        <div>
          <div className="text-xs text-muted-foreground">{label}</div>
          {href ? (
            <a href={href} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-primary hover:underline">
              {value}
            </a>
          ) : (
            <div className="text-sm font-medium">{value}</div>
          )}
        </div>
      </div>
      {copyable && (
        <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => copyToClipboard(value, label)}>
          <Copy className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

function ContactInfoTab() {
  return (
    <div className="grid gap-6 md:grid-cols-2">
      {/* ATEK Account Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Building2 className="h-4 w-4" /> ATEK Account
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ContactInfoRow icon={Building2} label="Company" value="10007668 CANADA INC." />
          <ContactInfoRow icon={FileText} label="DHL Account #" value="740104898RM0001" />
          <ContactInfoRow icon={User} label="Contact" value="Hakim Rouab" />
          <ContactInfoRow icon={Mail} label="Email" value="hakim@atek.io" href="mailto:hakim@atek.io" />
          <ContactInfoRow icon={Phone} label="Phone" value="514-501-6294" href="tel:5145016294" />
          <ContactInfoRow icon={MapPin} label="Address" value="240-5490 boul Thimens, H4R 2K9, St-Laurent, QC" />
        </CardContent>
      </Card>

      {/* DHL Contact Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Phone className="h-4 w-4" /> DHL Contacts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ContactInfoRow icon={Phone} label="DHL Canada (Customer Service)" value="1-855-345-7447" href="tel:18553457447" />
          <ContactInfoRow icon={User} label="Livreur local (Michel)" value="514-233-4555" href="tel:5142334555" />
        </CardContent>
      </Card>

      {/* Quick Links */}
      <Card className="md:col-span-2">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Globe className="h-4 w-4" /> Quick Links
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <Button variant="outline" className="justify-start h-auto py-3" asChild>
              <a href="https://www.dhl.com/ca-en/home/tracking/tracking-express.html" target="_blank" rel="noopener noreferrer">
                <Search className="h-4 w-4 mr-2 shrink-0" />
                <div className="text-left">
                  <div className="text-sm font-medium">Track Shipment</div>
                  <div className="text-xs text-muted-foreground">DHL Express Tracking</div>
                </div>
              </a>
            </Button>
            <Button variant="outline" className="justify-start h-auto py-3" asChild>
              <a href="https://delivery.dhl.com/waybill.xhtml?ctrycode=CA" target="_blank" rel="noopener noreferrer">
                <FileText className="h-4 w-4 mr-2 shrink-0" />
                <div className="text-left">
                  <div className="text-sm font-medium">Pay Waybill</div>
                  <div className="text-xs text-muted-foreground">Online waybill payment</div>
                </div>
              </a>
            </Button>
            <Button variant="outline" className="justify-start h-auto py-3" asChild>
              <a href="https://mydhl.express.dhl/ca/en/forms/clearance-for-businesses.html" target="_blank" rel="noopener noreferrer">
                <Plane className="h-4 w-4 mr-2 shrink-0" />
                <div className="text-left">
                  <div className="text-sm font-medium">Customs Clearance</div>
                  <div className="text-xs text-muted-foreground">Clearance for businesses</div>
                </div>
              </a>
            </Button>
            <Button variant="outline" className="justify-start h-auto py-3" asChild>
              <a href="https://mydhl.express.dhl/ca/en/home.html" target="_blank" rel="noopener noreferrer">
                <Globe className="h-4 w-4 mr-2 shrink-0" />
                <div className="text-left">
                  <div className="text-sm font-medium">MyDHL+</div>
                  <div className="text-xs text-muted-foreground">Ship & manage shipments</div>
                </div>
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tips */}
      <Card className="md:col-span-2 bg-muted/50">
        <CardContent className="pt-6">
          <div className="flex gap-3">
            <Info className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground space-y-1">
              <p><strong>Customs Clearance:</strong> When importing goods, DHL will contact you for clearance. Have your waybill number and goods description ready.</p>
              <p><strong>Michel (local driver):</strong> For pickup scheduling or delivery issues in the Montreal area, contact Michel directly.</p>
              <p><strong>Account #:</strong> Use <span className="font-mono">740104898RM0001</span> when calling DHL or filling out forms.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// =============================================================================
// Tracking Tab
// =============================================================================

function getEventIcon(statusCode: string) {
  const code = (statusCode || '').toLowerCase();
  if (code === 'delivered') return <CheckCircle2 className="h-4 w-4 text-green-600" />;
  if (code === 'transit') return <Truck className="h-4 w-4 text-blue-600" />;
  if (code === 'out-for-delivery') return <ArrowRight className="h-4 w-4 text-yellow-600" />;
  if (code === 'customs') return <Plane className="h-4 w-4 text-purple-600" />;
  return <Package className="h-4 w-4 text-gray-400" />;
}

function getStatusBadge(shipment: DhlTrackingShipment) {
  if (shipment.actualDeliveryDate || shipment.status?.statusCode === 'delivered') {
    return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Delivered</Badge>;
  }
  const code = (shipment.status?.statusCode || '').toLowerCase();
  if (code === 'transit') return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">In Transit</Badge>;
  if (code === 'out-for-delivery') return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Out for Delivery</Badge>;
  if (code === 'customs') return <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">Customs</Badge>;
  if (code === 'failure') return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Exception</Badge>;
  return <Badge className="bg-gray-100 text-gray-800 hover:bg-gray-100">Pending</Badge>;
}

function fmtDateTime(dateStr: string | null): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleString('en-CA', {
      timeZone: 'America/Montreal', month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return dateStr; }
}

function fmtDate(dateStr: string | null): string {
  if (!dateStr) return '--';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-CA', {
      timeZone: 'America/Montreal', month: 'long', day: 'numeric', year: 'numeric',
    });
  } catch { return dateStr; }
}

function TrackingCard({ shipment }: { shipment: DhlTrackingShipment }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CardTitle className="font-mono text-lg">{shipment.id}</CardTitle>
            {getStatusBadge(shipment)}
          </div>
          <Button variant="outline" size="sm" asChild>
            <a href={getDhlTrackingUrl(shipment.id)} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="h-4 w-4 mr-1" /> DHL
            </a>
          </Button>
        </div>
        <div className="flex gap-6 text-sm text-muted-foreground mt-1">
          {shipment.service && <span>{shipment.service}</span>}
          {shipment.origin?.addressLocality && shipment.destination?.addressLocality && (
            <span className="flex items-center gap-1">
              {shipment.origin.addressLocality} <ArrowRight className="h-3 w-3" /> {shipment.destination.addressLocality}
            </span>
          )}
          {shipment.actualDeliveryDate ? (
            <span className="text-green-700 font-medium">Delivered {fmtDate(shipment.actualDeliveryDate)}</span>
          ) : shipment.estimatedDeliveryDate ? (
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> Expected {fmtDate(shipment.estimatedDeliveryDate)}</span>
          ) : null}
        </div>
      </CardHeader>
      <CardContent>
        {shipment.events.length === 0 ? (
          <p className="text-muted-foreground text-sm py-4 text-center">No tracking events available</p>
        ) : (
          <div className="relative pl-8">
            <div className="absolute left-[15px] top-2 bottom-2 w-px bg-border" />
            {shipment.events.map((event: DhlTrackingEvent, index: number) => (
              <div key={index} className="relative pb-5 last:pb-0">
                <div className="absolute left-[-22px] top-0.5 bg-background p-0.5">{getEventIcon(event.statusCode)}</div>
                <div>
                  <div className="text-sm font-medium">{event.description}</div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                    <span>{fmtDateTime(event.timestamp)}</span>
                    {event.location && (
                      <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{event.location}</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function TrackingTab() {
  const [input, setInput] = useState('');
  const [trackingNumbers, setTrackingNumbers] = useState<string[]>([]);

  const trackingQuery = useQuery({
    queryKey: ['dhl-tracking-lookup', trackingNumbers],
    queryFn: () => getDhlTracking(trackingNumbers),
    enabled: trackingNumbers.length > 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = input.split(/[\s,;]+/).map((s) => s.trim().replace(/\s/g, '')).filter(Boolean);
    if (parsed.length > 0) setTrackingNumbers(parsed);
  };

  const results = trackingQuery.data || [];

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex gap-3">
            <Input
              placeholder="Enter DHL tracking number(s) — separate multiple with commas"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="flex-1"
            />
            <Button type="submit" disabled={!input.trim() || trackingQuery.isFetching}>
              <Search className="h-4 w-4 mr-1" /> Track
            </Button>
          </form>
        </CardContent>
      </Card>

      {trackingQuery.isFetching && (
        <div className="space-y-4">
          {trackingNumbers.map((num) => (
            <Card key={num}>
              <CardHeader><Skeleton className="h-6 w-48" /><Skeleton className="h-4 w-32 mt-2" /></CardHeader>
              <CardContent><div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div></CardContent>
            </Card>
          ))}
        </div>
      )}

      {!trackingQuery.isFetching && results.length > 0 && (
        <div className="space-y-4">{results.map((s) => <TrackingCard key={s.id} shipment={s} />)}</div>
      )}

      {!trackingQuery.isFetching && trackingNumbers.length > 0 && results.length === 0 && !trackingQuery.error && (
        <Card>
          <CardContent className="py-12 text-center">
            <Package className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No tracking results found for the provided number(s).</p>
          </CardContent>
        </Card>
      )}

      {trackingQuery.error && (
        <Card><CardContent className="py-8 text-center"><p className="text-red-500">Error loading tracking data. Please try again.</p></CardContent></Card>
      )}

      {trackingNumbers.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center">
            <Search className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Enter a DHL tracking number above to look up delivery status.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// =============================================================================
// Rate Calculator Tab
// =============================================================================

function RateCalculatorTab() {
  const [originCountry, setOriginCountry] = useState('CA');
  const [originCity, setOriginCity] = useState('Saint-Laurent');
  const [originPostal, setOriginPostal] = useState('H4R 2K9');
  const [destCountry, setDestCountry] = useState('');
  const [destCity, setDestCity] = useState('');
  const [destPostal, setDestPostal] = useState('');
  const [weight, setWeight] = useState('1');
  const [length, setLength] = useState('');
  const [width, setWidth] = useState('');
  const [height, setHeight] = useState('');
  const [customsDeclarable, setCustomsDeclarable] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const queryParams = useMemo(() => {
    if (!submitted) return null;
    const w = parseFloat(weight);
    if (!originCountry || !originPostal || !destCountry || !w || w <= 0) return null;

    const params: Record<string, string | number | boolean> = {
      originCountryCode: originCountry,
      originCity,
      originPostalCode: originPostal.replace(/\s/g, ''),
      destCountryCode: destCountry,
      destCity,
      destPostalCode: destPostal.replace(/\s/g, ''),
      weight: w,
      isCustomsDeclarable: customsDeclarable,
    };

    const l = parseFloat(length), wi = parseFloat(width), h = parseFloat(height);
    if (l > 0 && wi > 0 && h > 0) {
      params.length = l;
      params.width = wi;
      params.height = h;
    }
    return params;
  }, [submitted, originCountry, originCity, originPostal, destCountry, destCity, destPostal, weight, length, width, height, customsDeclarable]);

  const ratesQuery = useQuery<DhlRateResult[]>({
    queryKey: ['dhl-rates', queryParams],
    queryFn: () => getDhlRates(queryParams!),
    enabled: !!queryParams,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <Label>Origin Country Code</Label>
                <Input value={originCountry} onChange={(e) => { setOriginCountry(e.target.value.toUpperCase()); setSubmitted(false); }} placeholder="CA" maxLength={2} />
              </div>
              <div>
                <Label>Origin City</Label>
                <Input value={originCity} onChange={(e) => { setOriginCity(e.target.value); setSubmitted(false); }} placeholder="Saint-Laurent" />
              </div>
              <div>
                <Label>Origin Postal Code</Label>
                <Input value={originPostal} onChange={(e) => { setOriginPostal(e.target.value); setSubmitted(false); }} placeholder="H4R 2K9" />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <Label>Destination Country Code</Label>
                <Input value={destCountry} onChange={(e) => { setDestCountry(e.target.value.toUpperCase()); setSubmitted(false); }} placeholder="US" maxLength={2} />
              </div>
              <div>
                <Label>Destination City</Label>
                <Input value={destCity} onChange={(e) => { setDestCity(e.target.value); setSubmitted(false); }} placeholder="New York" />
              </div>
              <div>
                <Label>Destination Postal Code</Label>
                <Input value={destPostal} onChange={(e) => { setDestPostal(e.target.value); setSubmitted(false); }} placeholder="10001" />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label>Weight (kg)</Label>
                <Input type="number" step="0.1" min="0.01" value={weight} onChange={(e) => { setWeight(e.target.value); setSubmitted(false); }} />
              </div>
              <div>
                <Label>Length (cm)</Label>
                <Input type="number" step="0.1" value={length} onChange={(e) => { setLength(e.target.value); setSubmitted(false); }} placeholder="Optional" />
              </div>
              <div>
                <Label>Width (cm)</Label>
                <Input type="number" step="0.1" value={width} onChange={(e) => { setWidth(e.target.value); setSubmitted(false); }} placeholder="Optional" />
              </div>
              <div>
                <Label>Height (cm)</Label>
                <Input type="number" step="0.1" value={height} onChange={(e) => { setHeight(e.target.value); setSubmitted(false); }} placeholder="Optional" />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="customs-declarable"
                checked={customsDeclarable}
                onCheckedChange={(checked) => { setCustomsDeclarable(checked === true); setSubmitted(false); }}
              />
              <Label htmlFor="customs-declarable" className="cursor-pointer">Customs Declarable</Label>
            </div>

            <Button type="submit" disabled={ratesQuery.isFetching}>
              <Calculator className="h-4 w-4 mr-1" /> Get Rates
            </Button>
          </form>
        </CardContent>
      </Card>

      {ratesQuery.isFetching && (
        <Card><CardContent className="py-6"><div className="space-y-3">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-16 w-full" />)}</div></CardContent></Card>
      )}

      {ratesQuery.error && (
        <Card><CardContent className="py-8 text-center"><p className="text-red-500">Error fetching rates: {(ratesQuery.error as Error).message}</p></CardContent></Card>
      )}

      {!ratesQuery.isFetching && ratesQuery.data && ratesQuery.data.length > 0 && (
        <div className="grid gap-3">
          {ratesQuery.data.map((rate) => (
            <Card key={rate.productCode}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{rate.productName}</div>
                    <div className="text-sm text-muted-foreground flex items-center gap-3">
                      <span className="font-mono">{rate.productCode}</span>
                      {rate.estimatedDeliveryDate && <span>Est. {fmtDate(rate.estimatedDeliveryDate)}</span>}
                    </div>
                  </div>
                  <div className="text-2xl font-bold tabular-nums">
                    {rate.currency === 'CAD' ? '$' : rate.currency + ' '}{rate.price.toFixed(2)}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!ratesQuery.isFetching && ratesQuery.data && ratesQuery.data.length === 0 && submitted && (
        <Card><CardContent className="py-8 text-center"><p className="text-muted-foreground">No rates available for this route.</p></CardContent></Card>
      )}

      {!submitted && (
        <Card>
          <CardContent className="py-16 text-center">
            <Calculator className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Enter origin, destination and weight to get DHL Express shipping rates.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// =============================================================================
// Service Points Tab
// =============================================================================

function ServicePointsTab() {
  const [countryCode, setCountryCode] = useState('CA');
  const [postalCode, setPostalCode] = useState('H4R 2K9');
  const [city, setCity] = useState('');
  const [maxResults, setMaxResults] = useState('10');
  const [submitted, setSubmitted] = useState(false);

  const queryParams = useMemo(() => {
    if (!submitted || !countryCode.trim() || !postalCode.trim()) return null;
    const params: Record<string, string | number> = {
      countryCode: countryCode.trim(),
      postalCode: postalCode.replace(/\s/g, ''),
      maxResults: parseInt(maxResults, 10) || 10,
    };
    if (city.trim()) params.city = city.trim();
    return params;
  }, [submitted, countryCode, postalCode, city, maxResults]);

  const pointsQuery = useQuery<DhlServicePoint[]>({
    queryKey: ['dhl-service-points', queryParams],
    queryFn: () => getDhlServicePoints(queryParams!),
    enabled: !!queryParams,
    staleTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="flex items-end gap-3 flex-wrap">
            <div>
              <Label>Country Code</Label>
              <Input
                value={countryCode}
                onChange={(e) => { setCountryCode(e.target.value.toUpperCase()); setSubmitted(false); }}
                placeholder="CA"
                maxLength={2}
                className="w-24"
              />
            </div>
            <div>
              <Label>Postal Code</Label>
              <Input
                value={postalCode}
                onChange={(e) => { setPostalCode(e.target.value); setSubmitted(false); }}
                placeholder="H4R 2K9"
                className="w-40"
              />
            </div>
            <div>
              <Label>City (optional)</Label>
              <Input
                value={city}
                onChange={(e) => { setCity(e.target.value); setSubmitted(false); }}
                placeholder="Saint-Laurent"
                className="w-40"
              />
            </div>
            <div>
              <Label>Max Results</Label>
              <Input
                type="number"
                min="1"
                max="50"
                value={maxResults}
                onChange={(e) => { setMaxResults(e.target.value); setSubmitted(false); }}
                className="w-24"
              />
            </div>
            <Button type="submit" disabled={!postalCode.trim() || pointsQuery.isFetching}>
              <Search className="h-4 w-4 mr-1" /> Find
            </Button>
          </form>
        </CardContent>
      </Card>

      {pointsQuery.isFetching && (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
      )}

      {pointsQuery.error && (
        <Card><CardContent className="py-8 text-center"><p className="text-red-500">Error finding service points.</p></CardContent></Card>
      )}

      {!pointsQuery.isFetching && pointsQuery.data && pointsQuery.data.length > 0 && (
        <div className="grid gap-3">
          {pointsQuery.data.map((point, index) => (
            <Card key={index}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-medium">{point.name}</div>
                    <div className="text-sm mt-1 flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {[point.address.street, point.address.city, point.address.postalCode].filter(Boolean).join(', ')}
                    </div>
                    {point.serviceTypes.length > 0 && (
                      <div className="flex gap-1.5 mt-2 flex-wrap">
                        {point.serviceTypes.map((type) => (
                          <Badge key={type} variant="secondary" className="text-xs">{type}</Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <Badge variant="outline" className="text-sm tabular-nums shrink-0">
                    {point.distance} m
                  </Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {!pointsQuery.isFetching && pointsQuery.data && pointsQuery.data.length === 0 && submitted && (
        <Card><CardContent className="py-8 text-center"><p className="text-muted-foreground">No service points found.</p></CardContent></Card>
      )}

      {!submitted && (
        <Card>
          <CardContent className="py-16 text-center">
            <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">Enter a postal code to find nearby DHL service points.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// =============================================================================
// Main Page
// =============================================================================

export default function DhlPage() {
  return (
    <div className="space-y-5">
      <div>
        <h1 className="page-title">
          DHL <small>Express</small>
        </h1>
      </div>

      <Tabs defaultValue="tracking" className="space-y-4">
        <TabsList>
          <TabsTrigger value="tracking">
            <Search className="h-4 w-4 mr-1.5" /> Tracking
          </TabsTrigger>
          <TabsTrigger value="rates">
            <Calculator className="h-4 w-4 mr-1.5" /> Rates
          </TabsTrigger>
          <TabsTrigger value="service-points">
            <Building2 className="h-4 w-4 mr-1.5" /> Service Points
          </TabsTrigger>
          <TabsTrigger value="account">
            <Info className="h-4 w-4 mr-1.5" /> Account
          </TabsTrigger>
        </TabsList>

        <TabsContent value="tracking">
          <TrackingTab />
        </TabsContent>

        <TabsContent value="rates">
          <RateCalculatorTab />
        </TabsContent>

        <TabsContent value="service-points">
          <ServicePointsTab />
        </TabsContent>

        <TabsContent value="account">
          <ContactInfoTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
